import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Share2, Lock, Eye, UserCheck, Bell, FileText, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const isJapanese = i18n.language === 'ja';

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/privacy-policy`);
        if (response.ok) {
          const data = await response.json();
          setPolicy(data);
        }
      } catch (error) {
        console.error('Error fetching privacy policy:', error);
      }
      setLoading(false);
    };
    fetchPolicy();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const sections = [
    { 
      icon: Database, 
      title: isJapanese ? '収集する情報' : 'Information We Collect', 
      content: policy?.information_collected,
      show: policy?.information_collected
    },
    { 
      icon: Eye, 
      title: isJapanese ? '情報の利用目的' : 'How We Use Your Information', 
      content: policy?.information_usage,
      show: policy?.information_usage
    },
    { 
      icon: Share2, 
      title: isJapanese ? '情報の共有・開示' : 'Information Sharing & Disclosure', 
      content: policy?.information_sharing,
      show: policy?.information_sharing
    },
    { 
      icon: UserCheck, 
      title: isJapanese ? '第三者への開示' : 'Third Party Disclosure', 
      content: policy?.third_party_disclosure,
      show: policy?.third_party_disclosure
    },
    { 
      icon: Lock, 
      title: isJapanese ? 'セキュリティ対策' : 'Security Measures', 
      content: policy?.security_measures,
      show: policy?.security_measures
    },
    { 
      icon: Shield, 
      title: isJapanese ? 'お客様の権利' : 'Your Rights', 
      content: policy?.user_rights,
      show: policy?.user_rights
    },
    { 
      icon: Bell, 
      title: isJapanese ? 'Cookieの使用' : 'Cookie Policy', 
      content: policy?.cookie_policy,
      show: policy?.cookie_policy
    },
    { 
      icon: FileText, 
      title: isJapanese ? '個人情報の保存期間' : 'Data Retention', 
      content: policy?.data_retention,
      show: policy?.data_retention
    },
    { 
      icon: HelpCircle, 
      title: isJapanese ? 'お問い合わせ' : 'Contact Information', 
      content: policy?.contact_info,
      show: policy?.contact_info
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="py-6 px-4 border-b border-gray-800">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{isJapanese ? 'ホームに戻る' : 'Back to Home'}</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-cyan-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {isJapanese ? 'プライバシーポリシー' : 'Privacy Policy'}
          </h1>
          <p className="text-gray-400">
            {isJapanese 
              ? '個人情報保護方針' 
              : 'How we collect, use, and protect your information'}
          </p>
          {policy?.last_updated && (
            <p className="text-gray-500 text-sm mt-2">
              {isJapanese ? '最終更新日: ' : 'Last Updated: '}
              {new Date(policy.last_updated).toLocaleDateString(isJapanese ? 'ja-JP' : 'en-US')}
            </p>
          )}
        </div>

        {/* Introduction */}
        {policy?.introduction && (
          <div className="mb-8 p-6 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl border border-cyan-500/20">
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
              {policy.introduction}
            </p>
          </div>
        )}

        {/* Policy Sections */}
        <div className="space-y-6">
          {sections.filter(s => s.show).map((section, index) => (
            <div 
              key={index}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-cyan-500/30 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <section.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Sections */}
          {policy?.custom_sections?.map((section, index) => (
            <div 
              key={`custom-${index}`}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-purple-500/30 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-purple-400 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Content Message */}
        {!policy?.introduction && !sections.some(s => s.show) && (
          <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
            <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              {isJapanese 
                ? 'プライバシーポリシーはまだ設定されていません。' 
                : 'Privacy policy information has not been set up yet.'}
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800 mt-12">
        <div className="max-w-4xl mx-auto text-center text-gray-500 text-sm">
          <Link to="/" className="hover:text-cyan-400 transition-colors">
            © {new Date().getFullYear()} Okinawa Sign Studio
          </Link>
        </div>
      </footer>
    </div>
  );
}
